import { useEffect, useRef, useState } from "react";
import { Briefcase, X, ChevronDown, ChevronUp, Pencil, Users } from "lucide-react";
import Pagination from "../components/Pagination.jsx";
import { listJobs, getJob } from "./thailandJobsApi.js";
import JobWorkersDialog from "./JobWorkersDialog.jsx";

const PAGE_SIZE = 10;

/**
 * The list defaults to the positions actually being recruited for. An operator
 * who opened งาน from a row wants to know what this employer is hiring right
 * now; the finished/cancelled ones are one click away, never mixed in by
 * default.
 */
const OPEN_ONLY_FILTER = "Open";

const JOB_STATUS_BADGE_VARIANT = {
  Draft: "neutral",
  Open: "warning",
  Paused: "warning",
  Closed: "neutral",
  Filled: "success",
  Cancelled: "danger",
};
const DEMAND_STATUS_BADGE_VARIANT = {
  NotReady: "neutral",
  ReadyForMatching: "info",
  PartiallyFilled: "warning",
  Filled: "success",
};

/**
 * Operator-facing wording for the two status columns. An unmapped value falls
 * back to the stored value rather than to a blank badge - a status this list
 * does not know about is still a real status, and hiding it would be worse
 * than showing it untranslated.
 */
const JOB_STATUS_LABEL = {
  Draft: "ร่าง",
  Open: "เปิดรับ",
  Paused: "พักรับสมัคร",
  Closed: "ปิดรับแล้ว",
  Filled: "ได้ครบแล้ว",
  Cancelled: "ยกเลิก",
};
const DEMAND_STATUS_LABEL = {
  NotReady: "ยังไม่พร้อมจับคู่",
  ReadyForMatching: "พร้อมจับคู่",
  PartiallyFilled: "ได้บางส่วน",
  Filled: "ได้ครบแล้ว",
};

const SALARY_PERIOD_LABEL = { Daily: "ต่อวัน", Monthly: "ต่อเดือน" };

const JOBS_DIALOG_NOTE = "รายการนี้คือความต้องการแรงงานที่นายจ้างรายนี้เปิดรับอยู่ ยังไม่ใช่การจับคู่แรงงาน — กดที่ตำแหน่งงานเพื่อดูรายละเอียดเต็ม";

function jobStatusBadgeVariant(status) {
  return JOB_STATUS_BADGE_VARIANT[status] ?? "neutral";
}

function demandStatusBadgeVariant(status) {
  return DEMAND_STATUS_BADGE_VARIANT[status] ?? "neutral";
}

function jobStatusLabel(status) {
  return JOB_STATUS_LABEL[status] ?? status ?? "-";
}

function demandStatusLabel(status) {
  return DEMAND_STATUS_LABEL[status] ?? status ?? "-";
}

/**
 * startDate/endDate arrive as plain YYYY-MM-DD (a DateOnly, not an instant).
 * Handing that string to `new Date()` parses it as midnight UTC and then
 * renders it in the browser's zone, which shows the day before for anyone west
 * of Greenwich - so the parts are read directly instead.
 */
function formatDateOnlyTh(value) {
  if (!value) {
    return "-";
  }
  const [year, month, day] = String(value).slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) {
    return "-";
  }
  return new Date(year, month - 1, day).toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
}

function formatSalary(detail) {
  if (detail.salaryAmount == null) {
    return "-";
  }
  const amount = Number(detail.salaryAmount).toLocaleString("th-TH");
  const currency = detail.salaryCurrency || "THB";
  const period = detail.salaryPeriod ? ` ${SALARY_PERIOD_LABEL[detail.salaryPeriod] ?? detail.salaryPeriod}` : "";
  return `${amount} ${currency}${period}`;
}

function describeJobsError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ดูงานของนายจ้างรายนี้";
  }
  return "ไม่สามารถโหลดรายการงานได้ กรุณาลองใหม่อีกครั้ง";
}

/**
 * The งาน dialog: every position one employer is recruiting for, opened by the
 * งาน button on that employer's own row.
 *
 * It reads the real Jobs list API filtered by this employerId - the same
 * endpoint and the same `employerId` filter the Jobs screen itself uses, so
 * there is no second definition of "this employer's jobs" - and fetches the
 * full job record on demand when a position is expanded. Creating a job
 * happens in place here (the shared CreateJobView, posting the same P101
 * Create Job API the drawer's งาน tab posts), so an operator who came to look
 * at the open positions never has to leave to add one.
 */
export default function EmployerJobsDialog({
  open,
  employerId,
  employerName,
  employerCode,
  canCreateJob,
  createJobDisabledReason,
  onCreateJob,
  // P161: opening one position for editing. Owned by the page (it owns the
  // job builder), so this dialog only ever says which job was asked for.
  onEditJob,
  reloadToken,
  onClose,
}) {
  const dialogRef = useRef(null);
  const [statusFilter, setStatusFilter] = useState(OPEN_ONLY_FILTER);
  const [page, setPage] = useState(1);
  const [state, setState] = useState({
    status: "idle",
    items: [],
    totalCount: 0,
    error: null,
  });
  const [expandedJobId, setExpandedJobId] = useState(null);
  const [jobDetail, setJobDetail] = useState({
    status: "idle",
    data: null,
    error: null,
    forId: null,
  });
  // P161: which position's worker list is open, or null. Held here rather than
  // on the page because it is opened from a row inside this dialog.
  const [workersForJob, setWorkersForJob] = useState(null);

  // Reset per-open so reopening for a different employer never shows the
  // previous one's positions for a frame.
  useEffect(() => {
    if (!open) {
      setStatusFilter(OPEN_ONLY_FILTER);
      setPage(1);
      setState({ status: "idle", items: [], totalCount: 0, error: null });
      setExpandedJobId(null);
      setJobDetail({ status: "idle", data: null, error: null, forId: null });
      setWorkersForJob(null);
    }
  }, [open]);

  useEffect(() => {
    setPage(1);
    setExpandedJobId(null);
  }, [statusFilter, employerId]);

  useEffect(() => {
    if (!open || !employerId) {
      return undefined;
    }
    let cancelled = false;
    setState((previous) => ({ ...previous, status: "loading", error: null }));

    listJobs({ employerId, page, pageSize: PAGE_SIZE, jobStatus: statusFilter })
      .then((data) => {
        if (!cancelled) {
          setState({
            status: "ready",
            items: data?.items ?? [],
            totalCount: data?.totalCount ?? 0,
            error: null,
          });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ status: "error", items: [], totalCount: 0, error });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, employerId, page, statusFilter, reloadToken]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  function toggleJob(jobId) {
    if (expandedJobId === jobId) {
      setExpandedJobId(null);
      return;
    }
    setExpandedJobId(jobId);
    if (jobDetail.forId === jobId && jobDetail.status === "ready") {
      return;
    }
    setJobDetail({ status: "loading", data: null, error: null, forId: jobId });
    getJob(jobId)
      .then((data) => setJobDetail({ status: "ready", data, error: null, forId: jobId }))
      .catch((error) => setJobDetail({ status: "error", data: null, error, forId: jobId }));
  }

  if (!open) {
    return null;
  }

  const totalPages = Math.max(1, Math.ceil(state.totalCount / PAGE_SIZE));
  const heading = employerName || "นายจ้างรายนี้";

  return (
    <div
      className="wx-tjd-root"
      onMouseDown={(event) => {
        if (dialogRef.current && !dialogRef.current.contains(event.target)) {
          onClose();
        }
      }}
    >
      <div className="wx-tjd-backdrop" aria-hidden="true" />
      <div ref={dialogRef} className="wx-tjd-dialog" role="dialog" aria-modal="true" aria-label={`ตำแหน่งงานของ ${heading}`}>
        <div className="wx-tjd-head">
          <div className="wx-tjd-head-text">
            <h2>
              <Briefcase size={16} aria-hidden="true" /> งานของ {heading}
            </h2>
            <p>
              {employerCode ?? "-"}
              {state.status === "ready" ? ` · ${state.totalCount.toLocaleString("en-US")} ตำแหน่งงาน` : " · กำลังโหลด..."}
            </p>
          </div>
          <div className="wx-tjd-head-actions">
            <button
              type="button"
              className="wx-button wx-button--primary wx-tjd-create-button"
              disabled={!canCreateJob}
              title={canCreateJob ? undefined : createJobDisabledReason}
              onClick={onCreateJob}
            >
              + สร้างงานใหม่
            </button>
            <button type="button" className="wx-tjd-close" onClick={onClose} aria-label="ปิด">
              <X size={16} aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="wx-tjd-filters">
          <button
            type="button"
            className={`wx-tjd-chip${statusFilter === OPEN_ONLY_FILTER ? " wx-tjd-chip--active" : ""}`}
            onClick={() => setStatusFilter(OPEN_ONLY_FILTER)}
          >
            ตำแหน่งที่เปิดรับ
          </button>
          <button type="button" className={`wx-tjd-chip${statusFilter === "" ? " wx-tjd-chip--active" : ""}`} onClick={() => setStatusFilter("")}>
            ทุกสถานะ
          </button>
        </div>

        <div className="wx-tjd-body">
          <p className="wx-tjd-note">{JOBS_DIALOG_NOTE}</p>

          {state.status === "loading" && state.items.length === 0 && <p className="wx-tjd-state">กำลังโหลดตำแหน่งงาน...</p>}

          {state.status === "error" && (
            <p className="wx-tjd-state wx-tjd-state--error" role="alert">
              {describeJobsError(state.error)}
            </p>
          )}

          {state.status === "ready" && state.items.length === 0 && (
            <p className="wx-tjd-state">
              {statusFilter === OPEN_ONLY_FILTER ? "นายจ้างรายนี้ยังไม่มีตำแหน่งงานที่เปิดรับอยู่" : "นายจ้างรายนี้ยังไม่มีงานในระบบ"}
            </p>
          )}

          {state.items.map((item) => (
            <JobRow
              key={item.jobId}
              item={item}
              expanded={expandedJobId === item.jobId}
              detail={jobDetail.forId === item.jobId ? jobDetail : null}
              onToggle={() => toggleJob(item.jobId)}
              onEdit={onEditJob ? () => onEditJob(item) : null}
              onViewWorkers={() => setWorkersForJob(item)}
            />
          ))}
        </div>

        {state.totalCount > PAGE_SIZE && (
          <Pagination page={page} totalPages={totalPages} totalCount={state.totalCount} pageSize={PAGE_SIZE} onPageChange={setPage} />
        )}
      </div>

      {/* Opens over this dialog rather than replacing its body: an operator who
          checks who is in a position should come back to the list they were
          reading, not lose their place in it. */}
      <JobWorkersDialog open={workersForJob !== null} job={workersForJob} onClose={() => setWorkersForJob(null)} />
    </div>
  );
}

function JobRow({ item, expanded, detail, onToggle, onEdit, onViewWorkers }) {
  return (
    <div className={`wx-tjd-job${expanded ? " wx-tjd-job--open" : ""}`}>
      <button type="button" className="wx-tjd-job-head" onClick={onToggle} aria-expanded={expanded}>
        <span className="wx-tjd-job-title">
          <strong>{item.jobTitle}</strong>
          <span>{item.jobCode}</span>
        </span>
        <span className="wx-tjd-job-counts">
          <strong>
            {item.requiredWorkerCount} / {item.filledWorkerCount} / {item.remainingWorkerCount}
          </strong>
          <span>ต้องการ / ได้แล้ว / เหลือ</span>
        </span>
        <span className="wx-tjd-job-badges">
          <span className={`wx-badge wx-badge--${jobStatusBadgeVariant(item.jobStatus)}`}>{jobStatusLabel(item.jobStatus)}</span>
          <span className={`wx-badge wx-badge--${demandStatusBadgeVariant(item.demandStatus)}`}>{demandStatusLabel(item.demandStatus)}</span>
        </span>
        <span className="wx-tjd-job-chevron" aria-hidden="true">
          {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </span>
      </button>

      {/* P161: the two things an operator wants from a position - change it, or
          see who is in it. Outside the expand button, because a button inside a
          button is not a thing, and both are useful without expanding. */}
      <div className="wx-tjd-job-actions">
        <button type="button" className="wx-button wx-button--ghost" onClick={onViewWorkers}>
          <Users size={14} aria-hidden="true" />
          ดูแรงงานในตำแหน่งนี้
        </button>
        {onEdit && (
          <button type="button" className="wx-button wx-button--ghost" onClick={onEdit}>
            <Pencil size={14} aria-hidden="true" />
            แก้ไขงาน
          </button>
        )}
      </div>

      {expanded && (
        <div className="wx-tjd-job-detail">
          {(!detail || detail.status === "loading") && <p className="wx-tjd-state">กำลังโหลดรายละเอียดตำแหน่งงาน...</p>}

          {detail?.status === "error" && (
            <p className="wx-tjd-state wx-tjd-state--error" role="alert">
              ไม่สามารถโหลดรายละเอียดตำแหน่งงานนี้ได้ กรุณาลองใหม่อีกครั้ง
            </p>
          )}

          {detail?.status === "ready" && <JobDetailBody detail={detail.data} />}
        </div>
      )}
    </div>
  );
}

/**
 * The expanded position.
 *
 * The facts are tiles (label above value, left aligned) rather than
 * label-left/value-right rows: across a 1180px dialog those rows put the
 * value half a screen from the word it answers, and a column of dashes for
 * everything unset read as a broken panel rather than as "not recorded".
 */
function JobDetailBody({ detail }) {
  const skills = detail.skillRequirements ?? [];
  const period = [formatDateOnlyTh(detail.startDate), formatDateOnlyTh(detail.endDate)].filter((part) => part !== "-").join(" - ");
  return (
    <>
      <div className="wx-tjd-facts">
        <Fact label="ค่าจ้าง" value={detail.salaryAmount == null ? null : formatSalary(detail)} empty="ยังไม่ระบุค่าจ้าง" />
        <Fact label="ช่วงเวลาทำงาน" value={period} empty="ยังไม่ระบุช่วงเวลา" />
        <Fact label="ภาษาที่ต้องการ" value={detail.languageRequirementText} empty="ไม่ได้ระบุ" />
        <Fact label="Agent เจ้าของงาน" value={`${detail.agent?.agentCode ?? "-"} · ${detail.agent?.displayName ?? "-"}`} />
      </div>

      <div className="wx-tjd-blocks">
        <Block label="รายละเอียดงาน" text={detail.jobDescription} empty="ยังไม่มีการบันทึกรายละเอียดงาน" />
        <Block label="สวัสดิการ" text={detail.benefitsText} empty="ยังไม่มีการบันทึกสวัสดิการ" />
      </div>

      <div className="wx-tjd-block">
        <span>ทักษะที่ต้องการ</span>
        {skills.length === 0 ? (
          <p className="wx-tjd-empty-text">ไม่ได้ระบุทักษะเฉพาะสำหรับตำแหน่งนี้</p>
        ) : (
          <ul className="wx-tjd-skill-list">
            {skills.map((skill) => (
              <li key={skill.requirementId ?? skill.skillCode}>
                <strong>{skill.skillName || skill.skillCode}</strong>
                <span className="wx-tjd-skill-count">{skill.requiredCount != null ? `${skill.requiredCount} อัตรา` : "ไม่ระบุจำนวน"}</span>
                {skill.minimumLevel && <span className="wx-tjd-skill-level">ระดับขั้นต่ำ {skill.minimumLevel}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

function Fact({ label, value, empty }) {
  const filled = value !== null && value !== undefined && value !== "";
  return (
    <div className={`wx-tjd-fact${filled ? "" : " wx-tjd-fact--empty"}`}>
      <span>{label}</span>
      <strong>{filled ? value : (empty ?? "-")}</strong>
    </div>
  );
}

function Block({ label, text, empty }) {
  return (
    <div className="wx-tjd-block">
      <span>{label}</span>
      <p className={text ? undefined : "wx-tjd-empty-text"}>{text || empty}</p>
    </div>
  );
}
